import { useRef, useLayoutEffect } from 'react'
import { posterUrl, profileUrl } from '../api/tmdb'

const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Crect fill='%23333' width='60' height='60' rx='30'/%3E%3C/svg%3E"
const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='60' viewBox='0 0 40 60'%3E%3Crect fill='%23333' width='40' height='60'/%3E%3C/svg%3E"

function SprocketRow({ position }) {
  return (
    <div className={`filmstrip__sprockets filmstrip__sprockets--${position}`} aria-hidden="true">
      {Array.from({ length: 60 }).map((_, i) => (
        <div key={i} className="filmstrip__hole" />
      ))}
    </div>
  )
}

export default function Filmstrip({ chain, onItemClick, hidePlaceholder = false, followLiveEdge = false }) {
  const lastIndex = chain.length - 1
  const trackRef = useRef(null)
  const mountedRef = useRef(false)

  // Initial resting position of the inner track. Only the track scrolls — the
  // sprocket frame is fixed — so this never affects page-level scroll.
  //   • First render / re-show (re-mount): always rest at the START (scrollLeft 0).
  //   • Chain contents change after mount:
  //       – review strips (followLiveEdge=false): reset to the START again, so a
  //         re-shown or swapped chain never keeps a stale scroll position.
  //       – in-game strip (followLiveEdge=true): follow the LIVE edge — keep the
  //         newest node (the player's latest hop) at the right edge instead of
  //         yanking back to the start actor on every hop.
  // useLayoutEffect runs before paint, so there's no visible mid-scroll flash.
  const chainKey = chain.map((c) => c.id ?? c.name ?? c.title ?? '').join('|')
  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return
    const isFirstRender = !mountedRef.current
    mountedRef.current = true
    if (followLiveEdge && !isFirstRender) {
      track.scrollLeft = track.scrollWidth   // follow the newest node at the right edge
    } else {
      track.scrollLeft = 0                   // rest at the start
    }
  }, [chainKey, followLiveEdge])

  return (
    <div className="filmstrip" aria-label="Connection chain">
      <SprocketRow position="top" />

      <div className="filmstrip__track" ref={trackRef}>
        {chain.map((item, i) => {
          const isClickable = onItemClick && i < lastIndex
          return (
            <div key={i} className="filmstrip__cell">
              {i > 0 && <span className="filmstrip__arrow" aria-hidden="true">›</span>}
              <div
                className={isClickable ? 'filmstrip__item-btn' : undefined}
                onClick={isClickable ? () => onItemClick(i) : undefined}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={isClickable ? (e) => e.key === 'Enter' && onItemClick(i) : undefined}
              >
                <FilmstripItem item={item} />
              </div>
            </div>
          )
        })}
        {!hidePlaceholder && (
          <div className="filmstrip__cell">
            <span className="filmstrip__arrow" aria-hidden="true">›</span>
            <div className="filmstrip__placeholder">?</div>
          </div>
        )}
      </div>

      <SprocketRow position="bottom" />
    </div>
  )
}

function FilmstripItem({ item }) {
  if (item.type === 'actor') {
    const src = item.profile_path ? profileUrl(item.profile_path, 'w185') : ACTOR_PH
    return (
      <div className="filmstrip__actor" title={item.name}>
        <img src={src} alt={item.name} onError={(e) => { e.target.src = ACTOR_PH }} />
        <span>{item.name}</span>
      </div>
    )
  }
  const src = item.poster_path ? posterUrl(item.poster_path, 'w92') : MOVIE_PH
  return (
    <div className="filmstrip__movie" title={item.title}>
      <img src={src} alt={item.title} onError={(e) => { e.target.src = MOVIE_PH }} />
      <span>{item.title}</span>
    </div>
  )
}
